import {action, makeObservable, observable, runInAction} from "mobx";
import {
    TranslationModuleServiceInterface,
    TranslationModuleStoreInterface
} from "../../types";
import FilterStore from "../abstract.filter.store";
import {
    EntityTranslationImportRequest, EntityTranslationImportSaveRequest,
    TranslationEditRequest,
    TranslationImportRequest,
    TranslationImportSaveRequest,
} from "../../types/request";
import {Translation} from "../../types/modules";
import {AppStore} from "../app.store";
import {ResponseTypeEnum} from "../../types/enums";
import {ApiResponseHandler} from "../../handler/apiResponse.handler";

export class TranslationStore extends FilterStore implements TranslationModuleStoreInterface {
    public readonly _headers: string[] = [];
    public readonly _module: string = 'translation';
    public _items: Translation[] = [];

    public _current: number = 1;
    public _pageSize: number = 25;

    constructor(
        private readonly appStore: AppStore,
        private readonly service: TranslationModuleServiceInterface
    ) {
        super();

        makeObservable(this, {
            _current: observable,
            _pageSize: observable,
            _orderFieldName: observable,
            _orderDirection: observable,
            _headers: observable,
            _items: observable,
            _loading: observable,
            setTablePaginationData: action,
            setTableFilterData: action,
        });
    }

    public import = async (request: TranslationImportRequest) => {
        this.appStore.setLoading(true);
        return await this.service.import(request)
            .then(response => {
                if (response.data.existingTranslations.length > 0) {
                    this.appStore.setResponseMessage('global.message.file_import_successful', ResponseTypeEnum.SUCCESS);
                } else {
                    this.appStore.setResponseMessage('global.message.nothing_to_import', ResponseTypeEnum.SUCCESS);
                }

                return response;
            }).finally(() => {
                    this.appStore.setLoading(false);
                });
    }

    public entityImport = async (request: EntityTranslationImportRequest) => {
        this.appStore.setLoading(true);
        return await this.service.entityImport(request)
            .then(response => {
                if (response.data.existingTranslations.length > 0) {
                    this.appStore.setResponseMessage('global.message.file_import_successful', ResponseTypeEnum.SUCCESS);
                } else {
                    this.appStore.setResponseMessage('global.message.nothing_to_import', ResponseTypeEnum.SUCCESS);
                }

                return response;
            }).finally(() => {
                    this.appStore.setLoading(false);
                });
    }

    public getImport = async () => {
        return await this.service.getImport()
            .then(response => {
                if (response.data.existingTranslations.length > 0) {
                    this.appStore.setResponseMessage('global.message.continue_translation_import', ResponseTypeEnum.INFO);
                }
                return response;
            });
    }

    public getEntityImport = async () => {
        return await this.service.getEntityImport()
            .then(response => {
                if (response.data.existingTranslations.length > 0) {
                    this.appStore.setResponseMessage('global.message.continue_translation_import', ResponseTypeEnum.INFO);
                }
                return response;
            });
    }

    public saveImport = async (request: TranslationImportSaveRequest) => {
        this.appStore.setLoading(true);
        return await this.service.saveImport(request)
            .then(response => {
                this.appStore.setResponseMessage('global.message.import_saved_successfully', ResponseTypeEnum.SUCCESS);
                return response;
            }).finally(() => {
                    this.appStore.setLoading(false);
                });
    }

    public saveEntityImport = async (request: EntityTranslationImportSaveRequest) => {
        this.appStore.setLoading(true);
        return await this.service.saveEntityImport(request)
            .then(response => {
                this.appStore.setResponseMessage('global.message.import_saved_successfully', ResponseTypeEnum.SUCCESS);
                return response;
            }).finally(() => {
                    this.appStore.setLoading(false);
                });
    }

    public edit = async (request: TranslationEditRequest) => {
        this.appStore.setLoading(true);
        await this.service.edit(request).finally(() => {
            this.appStore.setLoading(false);
        });
        await this.list();
    }

    public list = async () => {
        runInAction(() => {
            this.loading = true;
        });

        this.service.list({ page: this.current, rows: this.pageSize, searchFields: this._searchFields })
            .then(({ data }) => {
                runInAction(() => {
                    this.items = data.translations
                    this.total = data.total
                    this.loading = false;
                })
            }).catch(error => {
                ApiResponseHandler.handleResponseRedirects(error);
            });
    }

    setTablePaginationData = ({ current, pageSize }: any) => {
        this.current = current;
        this.pageSize = pageSize;
    }

    public getExport = async () => {
        return await this.service.getExport();
    }

    public invalidateCache = async () => {
        this.appStore.setLoading(true);
        return await this.service.invalidateCache().then(async () => {
            await this.appStore.updateApplicationTranslations().then(() => {
                this.appStore.setResponseMessage('global.message.translations_invalidated', ResponseTypeEnum.SUCCESS);
            }).finally(() => {
                this.appStore.setLoading(false);
            });
        })
    }

    public getEntityExport = async () => {
        return await this.service.getEntityExport();
    }
}